import type React from 'react';
import { Barcode, Eye, PenLine, Printer, Trash2 } from 'lucide-react';
import type { PaperworkDragItem } from '@/lib/dnd';
import { setPaperworkDrag } from '@/lib/dnd';

/**
 * ============================================================================
 * ⭐⭐ ONE ROW, USED BY BOTH TRAYS
 * ============================================================================
 *
 * > Pete, 2026-09-10: *"Should all have the same look and feel if its a local
 * > repository or a sharepoint one?"* … *"the File... button on local doesn't
 * > match the Print, eyeball and delete on the sharepoint share."*
 *
 * ⛔⛔ HE IS DESCRIBING THE SYMPTOM OF TWO IMPLEMENTATIONS. `PaperworkSliver`
 * (SharePoint) and `ScanFolderPanel` (a folder on this PC) are separate
 * components that happen to look similar, and EVERY feature added this week
 * landed in one of them and not the other:
 *
 *   thumbnails · the label-print icon · drag-to-file · the scrolling body
 *
 * Each was found by Pete, in production, one at a time — §4.14.3 five times
 * over. The guard counting the population was a plaster; this is the repair.
 *
 * ⭐ THIS COMPONENT WAS ALREADY WRITTEN AND ALREADY SELF-CONTAINED. It lived
 * inside `PaperworkSliver` taking nothing but props, so the only thing keeping
 * it from the other tray was the file it happened to sit in.
 *
 * ⚠️ MOVED, NOT REWRITTEN. Every note below is the original reasoning — the
 * click-vs-drag rule, why nothing hides until hovered, why the bin's tooltip is
 * a prop. Rewriting it would have thrown away four separate bug fixes whose
 * reasons are only recorded here.
 * ============================================================================
 */

/**
 * ---- ⭐ ONE GESTURE PER JOB, AND ALL THREE ARE VISIBLE ----------------------
 *
 * Pete, 2026-08-25, on the hover-bin-plus-click-to-open version that shipped first:
 * *"I don't like the way we set it up. I would like the delete button always visible
 * and clickable and a preview button right next to it. Then that just leaves the drag
 * function to a driver."*
 *
 * ⛔ THE CARD BODY NO LONGER CLICKS. It did both — click previewed, drag filed — and a
 * drag that travels only a few pixels before the mouse comes up is delivered as a
 * CLICK. So a scheduler who grabbed a scan, thought better of it, and let go got a new
 * browser tab instead of nothing. The body is now drag and only drag; there is no
 * gesture left to misread.
 *
 * ⛔ AND NOTHING HIDES UNTIL HOVERED. The old bin was hover-only on the theory that a
 * column of bins invites a misclick. That theory costs more than it saves: a control
 * you cannot see is a control a scheduler does not know exists, and it is unreachable
 * on a touch screen entirely. The confirm dialog is what stops a bad delete — not
 * concealment.
 *
 * ⚠️ BOTH BUTTONS STOP THE EVENT AND BOTH ARE UNDRAGGABLE. Without stopPropagation the
 * click bubbles to the card; without draggable={false} a press-and-move that begins on
 * the button starts dragging the card underneath it, which is how you file a document
 * you were trying to bin.
 */
export function TrayCard({
    name, sub, icon, title, onPreview, onDelete, onPrint, printTitle,
    onPrintPaper, onSign, selected,
    onToggleSelected, dragItems,
    deleteTitle = 'Not paperwork — move it to the SharePoint recycle bin',
}: {
    name: string;
    sub: string;
    icon: React.ReactNode;
    title: string;
    onPreview?: () => void;
    onDelete?: () => void;
    /**
     * ⭐ SEND THIS ROW TO THE LABEL PRINTER (§1AK). Optional, and absent on
     * almost every row: only a stored `.zpl` gets it.
     *
     * ⛔ SCANS NEVER GET IT, even a `.zpl` sitting in the scan folder. The
     * print route reads by documentId, and a scan has none until it is stored —
     * offering the button there would produce a failure with no useful sentence
     * behind it.
     */
    onPrint?: () => void;
    printTitle?: string;
    /**
     * ⭐ THE OTHER PRINT — a scan to a normal printer, through the OS dialog.
     * ⛔ MUTUALLY EXCLUSIVE WITH `onPrint` AT THE CALL SITE: a row is either a
     * label or it is paperwork, never offered both, or the two icons become a
     * guess about which printer the file is going to.
     */
    onPrintPaper?: () => void;
    /** ⭐ Stamp it as received and save a signed copy. Never on a label. */
    onSign?: () => void;
    /**
     * ⚠️ THE TOOLTIP IS A PROP BECAUSE THE TWO BUTTONS DO DIFFERENT THINGS. Same icon,
     * same place: a scan goes to the SharePoint recycle bin, a stored document is
     * voided and its row survives. One shared wording would be wrong for one of them,
     * and the bin is exactly the control where a scheduler reads the tooltip.
     */
    deleteTitle?: string;
    /** ⭐ Ticked for a batch drag. */
    selected: boolean;
    onToggleSelected: () => void;
    /**
     * ⛔ WHAT THIS DRAG ACTUALLY CARRIES — computed at dragstart, not at render.
     * The selection can change between the two, and a payload frozen at render is a
     * drag that files what was ticked a moment ago.
     */
    dragItems: () => PaperworkDragItem[];
}) {
    return (
        <div
            draggable
            /**
             * ⭐⭐ DRAGGING A **TICKED** CARD DRAGS THE WHOLE SELECTION (2026-08-27).
             *
             * Pete: *"add checkboxes to each of the Scanned Paperwork items to allow us
             * to drag all the checked attachments to a driver card or appointment."*
             *
             * ⭐ AND DRAGGING AN **UNTICKED** ONE DRAGS ONLY IT, which is how every file
             * manager on every desktop behaves. ⚠️ The alternative — always dragging the
             * selection — means a scheduler who ticked three things last minute and then
             * grabs a fourth silently files four. The card under the cursor is the one
             * the hand is on; it wins unless it is part of the set.
             *
             * ⛔ THE TICKS ARE NOT CLEARED HERE. A drop can be refused (a cancelled load,
             * a closed appointment) and the scheduler must still have their selection to
             * aim somewhere else. Clearing on a SUCCESSFUL file is the panel's job.
             */
            onDragStart={(e) => setPaperworkDrag(e.dataTransfer, dragItems())}
            title={title}
            className={
                'flex cursor-grab items-start gap-1.5 rounded border px-2 py-1 '
                + 'active:cursor-grabbing hover:border-primary/50 '
                + (selected ? 'border-primary/60 bg-primary/5' : 'bg-background')
            }
        >
            {/**
              * ⚠️ `stopPropagation` ON THE LABEL, and `draggable={false}` under it. The
              * card above is draggable, and a mousedown on a checkbox inside a draggable
              * parent starts a drag instead of ticking the box on some browsers — the
              * tick then never happens and the control looks broken.
              */}
            <label
                className="mt-0.5 flex shrink-0 cursor-pointer items-center"
                draggable={false}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                title={selected ? 'Ticked — dragging any ticked card files them all' : 'Tick to file several at once'}
            >
                <input
                    type="checkbox"
                    className="h-3.5 w-3.5 cursor-pointer"
                    checked={selected}
                    onChange={onToggleSelected}
                />
            </label>
            <span className="mt-0.5 shrink-0">{icon}</span>
            <span className="min-w-0 flex-1">
                <span className="block truncate font-mono text-[11px]" title={name}>{name}</span>
                <span className="block truncate text-[10px] text-muted-foreground">{sub}</span>
            </span>
            {/**
              * ⚠️ A BARCODE, NOT A PRINTER GLYPH. The generic printer icon belongs
              * to the OS-dialog path (§1AK's four-path table), which is coming for
              * laser paperwork. If both used a printer the two would be
              * indistinguishable at 14px, and picking the wrong one sends a 4x6
              * label to a laser tray.
              *
              * ⛔ FIRST OF THE THREE, deliberately: the trash is last, and a new
              * control landing beside it is the one arrangement worth avoiding.
              * ⚠️ `stopPropagation` like its neighbours — the card is draggable.
              */}
            {onPrint && (
                <button
                    type="button"
                    draggable={false}
                    onClick={(e) => { e.stopPropagation(); onPrint(); }}
                    title={printTitle ?? 'Print to the label printer'}
                    aria-label={`Print label ${name}`}
                    className="mt-0.5 shrink-0 rounded p-0.5 text-muted-foreground
                               hover:bg-primary/10 hover:text-foreground"
                >
                    <Barcode className="h-3.5 w-3.5" />
                </button>
            )}
            {onPrintPaper && (
                <button
                    type="button"
                    draggable={false}
                    onClick={(e) => { e.stopPropagation(); onPrintPaper(); }}
                    title="Print this to a normal printer"
                    aria-label={`Print ${name}`}
                    className="mt-0.5 shrink-0 rounded p-0.5 text-muted-foreground
                               hover:bg-primary/10 hover:text-foreground"
                >
                    <Printer className="h-3.5 w-3.5" />
                </button>
            )}
            {onSign && (
                <button
                    type="button"
                    draggable={false}
                    onClick={(e) => { e.stopPropagation(); onSign(); }}
                    title="Stamp as received and save a signed copy"
                    aria-label={`Sign ${name}`}
                    className="mt-0.5 shrink-0 rounded p-0.5 text-muted-foreground
                               hover:bg-primary/10 hover:text-foreground"
                >
                    <PenLine className="h-3.5 w-3.5" />
                </button>
            )}
            {onPreview && (
                <button
                    type="button"
                    draggable={false}
                    onClick={(e) => { e.stopPropagation(); onPreview(); }}
                    title="Open it in a new tab to see what it is"
                    aria-label={`Preview ${name}`}
                    className="mt-0.5 shrink-0 rounded p-0.5 text-muted-foreground
                               hover:bg-primary/10 hover:text-foreground"
                >
                    <Eye className="h-3.5 w-3.5" />
                </button>
            )}
            {onDelete && (
                <button
                    type="button"
                    draggable={false}
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    title={deleteTitle}
                    aria-label={`Delete ${name}`}
                    className="mt-0.5 shrink-0 rounded p-0.5 text-muted-foreground
                               hover:bg-destructive/10 hover:text-destructive"
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </button>
            )}
        </div>
    );
}

